import React, { useCallback, useMemo, useState } from 'react';
import {
  Form,
  Input,
  Modal,
  Button,
  Switch,
  Tree,
  Popover,
  Tooltip
} from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import type { TreeDataNode } from 'antd';
import { useCampaignsListWithFoldersQuery } from '@hooks/queries/campaign';
import {
  toggleCampaignFolderCreationModal,
  toggleCopyModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { ReactComponent as MenuSecondarySvg } from '@assets/icons/menu-secondary.svg';
import { ReactComponent as SettingsSvg } from '@assets/icons/settings.svg';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import { useCopyFolderMutation } from '@hooks/queries/assets';

import './CopyModal.less';

type FormValues = {
  searchText: string;
  selectedCampaignId: string;
  selectedCampaignName: string;
  copyAssets: boolean;
  copyComments: boolean;
  includeMembers: boolean;
};

type CopyModalFolderProps = {
  visible: boolean;
  folderId: string;
  name: string;
  isPrivate: boolean;
};

function copyOptionsDescription(
  copyAssets: boolean,
  copyComments: boolean,
  includeMembers: boolean
): string {
  const parts: string[] = [];
  if (copyAssets) parts.push('media');
  if (copyComments) parts.push('comments');
  if (includeMembers) parts.push('members');

  if (parts.length === 0) return 'Only folder';
  if (parts.length === 1) return `Folder with ${parts[0]}`;

  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(', ');
  return `Folder with ${rest} and ${last}`;
}

export default function CopyModalFolder(props: CopyModalFolderProps) {
  const { visible, folderId, name, isPrivate } = props;
  const [currentWorkspace] = useCurrentWorkspace(false);
  const dispatch = useDispatch();
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const canCreateCampaign = currentWorkspace?.permissions?.createCampaigns;
  const copyAssets = Form.useWatch('copyAssets', form) ?? true;
  const copyComments = Form.useWatch('copyComments', form) ?? true;
  const includeMembers = Form.useWatch('includeMembers', form) ?? true;
  const selectedCampaignName = Form.useWatch('selectedCampaignName', form);

  const copyOptionsText = useMemo(
    () =>
      copyOptionsDescription(!!copyAssets, !!copyComments, !!includeMembers),
    [copyAssets, copyComments, includeMembers]
  );

  const searchText = Form.useWatch('searchText', form) ?? '';

  const { data: campaignsListQuery } = useCampaignsListWithFoldersQuery({
    params: {
      workspaceId: currentWorkspace?.id || ''
    }
  });

  const campaignsList: TreeDataNode[] = useMemo(() => {
    let _campaignsList = campaignsListQuery ?? [];
    if (searchText) {
      _campaignsList = _campaignsList.filter((campaign) => {
        return campaign.name
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase());
      });
    }
    return _campaignsList.map((item) => ({
      key: item.id,
      icon: (
        <MenuSecondarySvg className="b-icon-campaign" width={16} height={16} />
      ),
      title: item.name
    }));
  }, [campaignsListQuery, searchText]);

  const { mutateAsync: copyFolder, isPending: isCopyingFolder } =
    useCopyFolderMutation({});

  const onCloseModal = useCallback(() => {
    dispatch(toggleCopyModal(null));
  }, [dispatch]);

  const onCopyFolder = (data: FormValues) => {
    copyFolder(
      {
        workspaceId: currentWorkspace?.id || '',
        folderId,
        targetCampaignId: data.selectedCampaignId,
        copyAssets: data.copyAssets,
        copyComments: data.copyComments,
        includeMembers: data.includeMembers
      },
      {
        onSuccess: (res) => {
          if (res.data.jobId) {
            localStorage.setItem(
              `copy-link-${res.data.jobId}`,
              `/campaigns/single/${data.selectedCampaignId}/${res.data.newFolderId}`
            );
          }
          onCloseModal();
        }
      }
    );
  };

  const onCreateProject = useCallback(() => {
    if (!canCreateCampaign) return;
    dispatch(
      toggleCampaignFolderCreationModal({
        visible: true,
        entity: 'campaign',
        notShowCampaignCreatedModal: true
      })
    );
  }, [dispatch, canCreateCampaign]);

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={656}
      destroyOnClose
      zIndex={1000}
      closeIcon={<CrossSvg />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('copy-modal')}
    >
      <Form
        form={form}
        onFinish={onCopyFolder}
        validateTrigger="onChange"
        layout="vertical"
        initialValues={{
          copyAssets: true,
          copyComments: true,
          includeMembers: true
        }}
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 8 }}
        >
          Copy to
        </h2>
        {!selectedCampaignName && (
          <div className="copy-modal__top">
            Select a destination to copy
            <div className="copy-modal__name">
              {isPrivate ? (
                <PrivateFolderSvg
                  width={24}
                  height={24}
                  className="b-icon-folder"
                />
              ) : (
                <FolderSvg width={24} height={24} className="b-icon-folder" />
              )}
              <Tooltip
                placement="top"
                overlayClassName="toggle_favorite_overlay"
                title={name}
              >
                <div className="copy-modal__name-text">{name}</div>
              </Tooltip>
            </div>
          </div>
        )}
        {!!selectedCampaignName && (
          <div className="copy-modal__top">
            Copy
            <div className="copy-modal__name">
              {isPrivate ? (
                <PrivateFolderSvg
                  width={24}
                  height={24}
                  className="b-icon-folder"
                />
              ) : (
                <FolderSvg width={24} height={24} className="b-icon-folder" />
              )}
              <Tooltip
                placement="top"
                overlayClassName="toggle_favorite_overlay"
                title={name}
              >
                <div className="copy-modal__name-text">{name}</div>
              </Tooltip>
            </div>
            into
            <div className="copy-modal__name">
              <MenuSecondarySvg
                className="b-icon-campaign"
                width={24}
                height={24}
              />
              <Tooltip
                placement="top"
                overlayClassName="toggle_favorite_overlay"
                title={selectedCampaignName}
              >
                <div className="copy-modal__name-text">
                  {selectedCampaignName}
                </div>
              </Tooltip>
            </div>
          </div>
        )}
        <Form.Item name="searchText" className="copy-modal__search-input">
          <Input
            placeholder="Search by project"
            prefix={<SearchSvg />}
            size="large"
          />
          {canCreateCampaign && (
            <Button
              htmlType="button"
              type="ghost"
              className="copy-modal__action-button"
              onClick={onCreateProject}
              icon={<PlusSvg className="b-plus-icon" />}
            >
              New project
            </Button>
          )}
        </Form.Item>
        <Form.Item name="selectedCampaignName" hidden />
        <Form.Item name="copyAssets" valuePropName="checked" hidden />
        <Form.Item name="copyComments" valuePropName="checked" hidden />
        <Form.Item name="includeMembers" valuePropName="checked" hidden />
        <div className="copy-modal__tree-wrapper">
          <Tree
            treeData={campaignsList}
            showIcon={true}
            switcherIcon={<ArrowDownSvg width={20} height={20} />}
            onSelect={(_selectedKeys, { node }) => {
              form.setFieldValue(
                'selectedCampaignName',
                node.selected ? '' : (node.title as string)
              );
              form.setFieldsValue({
                selectedCampaignId: node.selected ? '' : (node.key as string)
              });
            }}
          />
        </div>
        <div
          className="copy-modal__options"
          onMouseEnter={() => setIsOptionsOpen(true)}
          onMouseLeave={() => setIsOptionsOpen(false)}
        >
          <div className="copy-modal__options-description">
            <div className="copy-modal__options-title">Copy options</div>
            <div className="copy-modal__options-text">{copyOptionsText}</div>
          </div>
          <Popover
            placement="topRight"
            getPopupContainer={(e) => e.parentElement as HTMLElement}
            content={
              <div>
                <div className="copy-modal__options-title">Copy options</div>
                <div
                  className="copy-modal__switch"
                  onClick={() => form.setFieldValue('copyAssets', !copyAssets)}
                >
                  <Switch checked={!!copyAssets} />
                  <span>Duplicate media</span>
                </div>
                <div
                  className="copy-modal__switch"
                  onClick={() =>
                    form.setFieldValue('copyComments', !copyComments)
                  }
                >
                  <Switch checked={!!copyComments} />
                  <span>Duplicate comments</span>
                </div>
                <div
                  className="copy-modal__switch"
                  onClick={() =>
                    form.setFieldValue('includeMembers', !includeMembers)
                  }
                >
                  <Switch checked={!!includeMembers} />
                  <span>Include members</span>
                </div>
              </div>
            }
            trigger="hover"
            showArrow={false}
            open={isOptionsOpen}
          >
            <SettingsSvg />
          </Popover>
        </div>
        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isCopyingFolder}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            Copy folder
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
